import type { CoachStep } from '../../../../../../types/QuestionCoach';
import {
  setCoachMarkActiveStyle,
  styleCoachMark,
  unstyleCoachMark,
} from './coachHighlightStyle';

/**
 * The DOM side of Question Coach (ORA-1091): marking a walkthrough step's
 * phrase in the question stem, or its answer option, and taking the marks
 * back off.
 *
 * Stem phrases are wrapped in `span.coach-highlight`, never the student's
 * `.highlight`, and `stripCoachHighlightsFromHtml` keeps them out of the
 * highlights saved from the stem. Answer options are rendered by React, so an
 * option is only marked with attributes on its row; wrapping its text would
 * move nodes React still owns. How a mark looks is `coachHighlightStyle`.
 */

export const COACH_HIGHLIGHT_CLASS = 'coach-highlight';
/** The step index on every mark (stem span or option row). */
export const COACH_STEP_ATTRIBUTE = 'data-coach-step';
/** On the marks of the step being narrated now. */
export const COACH_ACTIVE_ATTRIBUTE = 'data-coach-active';
/** The step's caption on every mark (the tooltip carries it as `title`). */
export const COACH_CAPTION_ATTRIBUTE = 'data-coach-caption';
/** The hook on each answer option row (`AnswerOptions`). */
export const ANSWER_OPTION_ID_ATTRIBUTE = 'data-answer-option-id';

// No `aria-label`: on an option row (a `<label>`) it would rename the radio,
// and a plain span may not carry one. The card's live region reads captions.
const COACH_MARK_ATTRIBUTES = [
  COACH_STEP_ATTRIBUTE,
  COACH_ACTIVE_ATTRIBUTE,
  COACH_CAPTION_ATTRIBUTE,
  'title',
];

const SKIPPED_ANCESTORS = '.katex, math, script, style, noscript';

const BLOCK_TAGS = new Set([
  'ADDRESS',
  'BLOCKQUOTE',
  'DD',
  'DIV',
  'DL',
  'DT',
  'FIGCAPTION',
  'FIGURE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'LI',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'TABLE',
  'TD',
  'TH',
  'TR',
  'UL',
]);

const SINGLE_QUOTES = /[‘’‚‛′]/g;
const DOUBLE_QUOTES = /[“”„‟″]/g;

const straightenQuotes = (text: string): string =>
  text.replace(SINGLE_QUOTES, "'").replace(DOUBLE_QUOTES, '"');

/**
 * How stem text and a step's quote are compared: NFC, straight quotes, every
 * whitespace run (non-breaking spaces included) collapsed to one space, and
 * trimmed.
 */
export const normalizeCoachText = (text: string): string =>
  straightenQuotes(text.normalize('NFC')).replace(/\s+/g, ' ').trim();

/** Where one character of the normalized text came from. */
type CharSource = { node: Text; start: number; end: number };

type NormalizedText = { text: string; sources: CharSource[] };

const nearestBlock = (node: Node, root: HTMLElement): Element | null => {
  let current = node.parentElement;

  while (current && current !== root) {
    if (BLOCK_TAGS.has(current.tagName)) return current;
    current = current.parentElement;
  }

  return root;
};

/** A base character with its combining marks, so NFC can compose them. */
const CLUSTER = /\P{M}\p{M}*|\p{M}+/gu;

/**
 * The container's visible text, normalized like `normalizeCoachText`, with
 * each character mapped back to the text node (and offsets) it came from.
 * Text inside rendered math is left out.
 */
const buildNormalizedText = (root: HTMLElement): NormalizedText => {
  let text = '';
  const sources: CharSource[] = [];
  let previousBlock: Element | null = null;

  const pushSpace = (source: CharSource) => {
    if (text.length === 0 || text.endsWith(' ')) return;
    text += ' ';
    sources.push(source);
  };

  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT
  );

  let pendingBreak = false;

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      // A line break or rule separates words the way a block boundary does
      // (the backend's stem text turns both into a space).
      const tag = (node as Element).tagName;
      if (tag === 'BR' || tag === 'HR') pendingBreak = true;
      continue;
    }

    const textNode = node as Text;
    if (textNode.parentElement?.closest(SKIPPED_ANCESTORS)) continue;

    const data = textNode.data;
    if (data.length === 0) continue;

    // Paragraphs and list items read as separate words even when the markup
    // puts no whitespace between them.
    const block = nearestBlock(textNode, root);
    if ((previousBlock && block !== previousBlock) || pendingBreak) {
      pushSpace({ node: textNode, start: 0, end: 0 });
    }
    previousBlock = block;
    pendingBreak = false;

    for (const match of data.matchAll(CLUSTER)) {
      const start = match.index ?? 0;
      const source = { node: textNode, start, end: start + match[0].length };
      const composed = straightenQuotes(match[0].normalize('NFC'));

      if (/^\s+$/.test(composed)) {
        pushSpace(source);
        continue;
      }

      for (const char of composed) {
        for (let unit = 0; unit < char.length; unit += 1) {
          sources.push(source);
        }
        text += char;
      }
    }
  }

  if (text.endsWith(' ')) {
    text = text.slice(0, -1);
    sources.pop();
  }

  return { text, sources };
};

/**
 * The text-node slices that hold `quote` in `root`, or null when the quote is
 * absent or appears more than once (an ambiguous mark teaches nothing).
 */
export const findCoachQuoteSlices = (
  root: HTMLElement,
  quote: string
): CharSource[] | null => {
  const needle = normalizeCoachText(quote);
  if (!needle) return null;

  const { text, sources } = buildNormalizedText(root);
  const index = text.indexOf(needle);
  if (index === -1) return null;
  if (text.indexOf(needle, index + 1) !== -1) return null;

  const matched = sources.slice(index, index + needle.length);
  const slices: CharSource[] = [];

  matched.forEach(({ node, start, end }) => {
    if (end <= start) return;

    const last = slices[slices.length - 1];
    if (last && last.node === node) {
      last.start = Math.min(last.start, start);
      last.end = Math.max(last.end, end);
      return;
    }

    slices.push({ node, start, end });
  });

  // A whitespace-only node between blocks (inside a list or table) is not
  // worth a span, and a span there would be invalid markup.
  return slices.filter(
    ({ node, start, end }) => node.data.slice(start, end).trim().length > 0
  );
};

const markStep = (element: HTMLElement, step: CoachStep, index: number) => {
  element.setAttribute(COACH_STEP_ATTRIBUTE, String(index));
  element.setAttribute('title', step.caption);
  element.setAttribute(COACH_CAPTION_ATTRIBUTE, step.caption);
};

const wrapSlice = ({ node, start, end }: CharSource): HTMLSpanElement => {
  let target = node;
  if (start > 0) target = target.splitText(start);
  if (end - start < target.data.length) target.splitText(end - start);

  const span = document.createElement('span');
  span.className = COACH_HIGHLIGHT_CLASS;
  target.parentNode?.insertBefore(span, target);
  span.appendChild(target);

  return span;
};

const hasStepMark = (root: HTMLElement, index: number): boolean =>
  root.querySelector(`[${COACH_STEP_ATTRIBUTE}="${index}"]`) !== null;

/**
 * Wraps a stem step's quote in `span.coach-highlight` (one span per text node
 * it crosses). Idempotent: a step already marked is left alone. Returns
 * whether the step is marked afterwards.
 */
export const applyCoachStemStep = (
  root: HTMLElement,
  step: CoachStep,
  index: number
): boolean => {
  if (hasStepMark(root, index)) return true;

  const slices = findCoachQuoteSlices(root, step.quote);
  if (!slices || slices.length === 0) return false;

  slices.forEach((slice) => {
    const span = wrapSlice(slice);
    markStep(span, step, index);
    styleCoachMark(span, false);
  });

  return true;
};

/**
 * Marks the answer option row a step points at, when the quote is that
 * option's text. Attributes only, so React keeps owning the row's children.
 */
export const applyCoachOptionStep = (
  root: HTMLElement,
  step: CoachStep,
  index: number
): boolean => {
  if (!step.optionId) return false;

  const row = Array.from(
    root.querySelectorAll<HTMLElement>(`[${ANSWER_OPTION_ID_ATTRIBUTE}]`)
  ).find(
    (element) =>
      element.getAttribute(ANSWER_OPTION_ID_ATTRIBUTE) === step.optionId
  );

  if (!row) return false;

  // Two steps can point at one option: the latest one reached keeps the row,
  // and a row already holding this step (or a later one) is left untouched,
  // so playback never rewrites it on every tick.
  const markedIndex = Number(row.getAttribute(COACH_STEP_ATTRIBUTE) ?? -1);
  if (markedIndex < index) {
    markStep(row, step, index);
    // A row taken over by a later step keeps its look; only a new mark sweeps.
    if (markedIndex === -1) {
      styleCoachMark(row, row.hasAttribute(COACH_ACTIVE_ATTRIBUTE));
    }
  }

  return true;
};

/** Points the active mark at `index` (null clears it) across `roots`. */
export const setActiveCoachStep = (
  roots: Array<HTMLElement | null | undefined>,
  index: number | null
): void => {
  roots.forEach((root) => {
    root?.querySelectorAll(`[${COACH_STEP_ATTRIBUTE}]`).forEach((element) => {
      const isActive =
        index !== null &&
        element.getAttribute(COACH_STEP_ATTRIBUTE) === String(index);

      if (isActive === element.hasAttribute(COACH_ACTIVE_ATTRIBUTE)) return;

      if (isActive) {
        element.setAttribute(COACH_ACTIVE_ATTRIBUTE, 'true');
      } else {
        element.removeAttribute(COACH_ACTIVE_ATTRIBUTE);
      }

      setCoachMarkActiveStyle(element, isActive);
    });
  });
};

const removeCoachMark = (element: Element) => {
  unstyleCoachMark(element);
  COACH_MARK_ATTRIBUTES.forEach((name) => element.removeAttribute(name));
};

const unwrapCoachSpans = (root: ParentNode) => {
  const touchedParents = new Set<Node>();

  root.querySelectorAll(`span.${COACH_HIGHLIGHT_CLASS}`).forEach((span) => {
    const parent = span.parentNode;
    if (!parent) return;

    // The student highlighted exactly a coach phrase, and the class applier
    // put `highlight` on the coach span instead of nesting a new one: keep
    // the span as the student's, without the coach class and attributes.
    if (span.classList.length > 1) {
      span.classList.remove(COACH_HIGHLIGHT_CLASS);
      removeCoachMark(span);
      return;
    }

    while (span.firstChild) parent.insertBefore(span.firstChild, span);
    parent.removeChild(span);
    touchedParents.add(parent);
  });

  touchedParents.forEach((parent) => parent.normalize());
};

/**
 * Takes every coach mark off `roots`: stem spans are unwrapped (text nodes
 * merged back), a span the student also highlighted keeps only that, and
 * option rows lose their attributes and styles.
 */
export const clearCoachHighlights = (
  roots: Array<HTMLElement | null | undefined>
): void => {
  roots.forEach((root) => {
    if (!root) return;

    unwrapCoachSpans(root);

    root.querySelectorAll(`[${COACH_STEP_ATTRIBUTE}]`).forEach(removeCoachMark);
  });
};

/**
 * The stem HTML without any coach marks, for the student's saved highlights.
 * HTML without a coach span is returned untouched, so a round trip through
 * the parser never rewrites markup that had nothing to strip.
 */
export const stripCoachHighlightsFromHtml = (html: string): string => {
  if (!html.includes(COACH_HIGHLIGHT_CLASS)) return html;

  const container = document.createElement('div');
  container.innerHTML = html;

  if (!container.querySelector(`span.${COACH_HIGHLIGHT_CLASS}`)) return html;

  unwrapCoachSpans(container);

  return container.innerHTML;
};
